import { useCallback, useState } from "react";
import { useLocation } from "react-router";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { GetAllObrasMateriaisControladosModel } from "../../domain/models/obras_materiaisc_ontrolados";
import { GetObraMateriaisControladosDropDown } from "../../data/daos/read/get_obra_materiais_controlados_dropdown";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { useObrasMateriaisControladosPagination } from "../hooks/Obras_ObrasMateriaisControlados/pagination";
import { ObrasMateriaisControladosForm } from "../schemas/Obras_ObrasMateriaisControlados/Obras_ObrasMateriaisControlados";

type LoadMateriaisFuncState = {
  idObra?: number;
  page?: number;
  rowsPerPage?: number;
};

export const useObrasMateriaisControladosViewModel = () => {
  const location = useLocation();
  const backOrNavigate = useBackOrNavigate();
  const { pushErrorNotification } = usePageLoader();
  const {
    obras: {
      getAllObrasMateriaisControladosUseCase,
      deleteObraMateriaisControladosUseCase,
      obraMateriaisControladosDropDownUseCase,
      createObraMateriaisControladosUseCase,
      getObraHeaderInfoUseCase,
    },
  } = useDependencyInjection();

  const [idObra, setIdObra] = useState<number>();
  const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();
  const [materiaisControlados, setMateriaisControlados] = useState<GetAllObrasMateriaisControladosModel[]>([]);
  const [materiaisControladosDropdown, setMateriaisControladosDropdown] = useState<GetObraMateriaisControladosDropDown[]>([]);
  const [selectedId, setSelectedId] = useState<number>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openIncluirModal, setOpenIncluirModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const obrasTopHeaderState = useObrasTopHeaderState();
  const [obrasReportState, initializeReportState] = useObrasCommonReports({ idObra });
  const pagination = useObrasMateriaisControladosPagination();

  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    const obra = await getObraHeaderInfoUseCase.getObraHeaderInfo(id ?? idObra);
    setSelectedObraHeaderInfo(obra);
  }, [getObraHeaderInfoUseCase, idObra]);

  const load = useCallback(async (state?: LoadMateriaisFuncState) => {
    const obraId = state?.idObra ?? idObra;
    const page = state?.page ?? pagination.page;
    const rowsPerPage = state?.rowsPerPage ?? pagination.rowsPerPage;

    await getAllObrasMateriaisControladosUseCase.getAllObrasMateriaisControlados(obraId, page, rowsPerPage);
    const dropdown = await obraMateriaisControladosDropDownUseCase.obraMateriaisControladosDropDown(obraId);
    setMateriaisControlados(getAllObrasMateriaisControladosUseCase.items);
    setMateriaisControladosDropdown(dropdown);
    pagination.setRowsPerPage(getAllObrasMateriaisControladosUseCase.pageSize);
    pagination.setTotalItems(getAllObrasMateriaisControladosUseCase.totalItems);
  }, [getAllObrasMateriaisControladosUseCase, obraMateriaisControladosDropDownUseCase, idObra, pagination]);

  usePageStartBootstrap(async () => {
    try {
      const id = Number(new URLSearchParams(location.search).get("id"));
      if (!Number.isInteger(id) || id <= 0) {
        setErrorMessage("Falha ao recuperar id da obra!");
        return;
      }
      setIdObra(id);
      useObrasMateriaisControladosPagination.setState({ page: 1 });
      await Promise.all([
        loadObraHeaderInfo(id),
        load({ idObra: id, page: 1 }),
        initializeReportState(id),
      ]);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da página!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    if (!idObra) return;
    try {
      setLoading(true);
      await Promise.all([loadObraHeaderInfo(idObra), load({ idObra }), initializeReportState(idObra)]);
      setErrorMessage(undefined);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da página!")[0]);
    } finally {
      setLoading(false);
    }
  }, [idObra, initializeReportState, load, loadObraHeaderInfo]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: "/Obras_lista" });
  }, [backOrNavigate]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      await load({ page: page + 1 });
      useObrasMateriaisControladosPagination.setState({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [load, pushErrorNotification]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      await load({ page: 1, rowsPerPage });
      useObrasMateriaisControladosPagination.setState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [load, pushErrorNotification]);

  const handleOpenIncluirModal = useCallback((open: boolean) => {
    setOpenIncluirModal(open);
  }, []);

  const handleDeleteOpenStateModal = useCallback((id: number | null, open: boolean) => {
    setSelectedId(id ?? undefined);
    setOpenDeleteModal(open);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (selectedId === undefined) return;
    try {
      setSaving(true);
      await deleteObraMateriaisControladosUseCase.deleteObraMateriaisControlados(selectedId);
      setOpenDeleteModal(false);
      const page = pagination.page > 1 && pagination.totalItems - 1 <= (pagination.page - 1) * pagination.rowsPerPage
        ? pagination.page - 1
        : pagination.page;
      await load({ page });
      useObrasMateriaisControladosPagination.setState({ page });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao excluir material controlado!"));
    } finally {
      setSaving(false);
    }
  }, [selectedId, deleteObraMateriaisControladosUseCase, load, pagination, pushErrorNotification]);

  const handleIncluirSubmit = useCallback(async (form: ObrasMateriaisControladosForm) => {
    try {
      setSaving(true);
      await createObraMateriaisControladosUseCase.createObraMateriaisControlados({
        idMateriaisControlados: form.idMateriaisControlados,
        idObra,
      });
      setOpenIncluirModal(false);
      await load();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao incluir materiais controlados!"));
    } finally {
      setSaving(false);
    }
  }, [createObraMateriaisControladosUseCase, idObra, load, pushErrorNotification]);

  return {
    obrasReportState,
    obrasTopHeaderState,
    selectedObraHeaderInfo,
    materiaisControlados,
    materiaisControladosDropdown,
    pagination,
    loading,
    saving,
    errorMessage,
    openDeleteModal,
    openIncluirModal,
    handleBack,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenIncluirModal,
    handleDeleteOpenStateModal,
    handleDeleteConfirm,
    handleIncluirSubmit,
  };
};
